import { Body, Button, Flex, useBreakpointValue } from '@hover/blueprint';
import { SimpleGrid } from '@hover/blueprint/chakra';
import React from 'react';

import type { VariationsGroupedByProvider } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilteredSearchResults/utils';
import { TestLabels } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/TestIds';
import { VariationThumbnail } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/VariationThumbnail';
import type { Variation } from 'src/modules/designStudio/helpers/productSelection/types';
import {
  TranslationNamespace,
  useIsLandscape,
  useTranslate,
} from 'src/shared/hooks';

export type ProviderVariationsParams = {
  onApplyVariation: (variation: Variation) => void;
  onShowViewAll?: () => void;
  onViewVariationDetails: (variation: Variation) => void;
  selectedVariation?: Variation;
  showViewAll: boolean;
  variationsGroupedByProvider: VariationsGroupedByProvider;
};

export const ProviderVariations = ({
  onApplyVariation,
  onShowViewAll,
  onViewVariationDetails,
  selectedVariation,
  showViewAll,
  variationsGroupedByProvider,
  ...other
}: ProviderVariationsParams) => {
  const [isLandscape] = useIsLandscape();
  const mobileColumns = useBreakpointValue({ base: 2, width250: 4 });

  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  return (
    <Flex
      aria-label={TestLabels.BRAND_GROUP}
      data-testid={variationsGroupedByProvider.provider.uuid}
      direction="column"
      gap="16px"
      {...other}
    >
      <Flex alignItems="center" direction="row">
        <Body fontSize="20px" fontWeight={700} my={0}>
          {variationsGroupedByProvider.provider.name}
        </Body>
        {showViewAll && (
          <Button
            aria-label={TestLabels.VIEW_ALL_VARIATIONS}
            marginLeft="auto"
            onClick={onShowViewAll}
            size="small"
            variant="ghost"
          >
            <Body fontSize="15px" fontWeight={400} my={0}>
              {t('productCatalogMenu.productDetails.viewAll')}
            </Body>
          </Button>
        )}
      </Flex>
      <SimpleGrid
        columns={isLandscape ? 2 : mobileColumns}
        display="grid"
        flexShrink={1}
        gap={400}
      >
        {variationsGroupedByProvider.variations.map(variation => (
          <VariationThumbnail
            isSelected={selectedVariation?.uuid === variation.uuid}
            key={`variation-${variation.uuid}`}
            maxWidth="156px"
            onApply={() => onApplyVariation(variation)}
            onViewMore={() => onViewVariationDetails(variation)}
            variation={variation}
            width="100%"
          />
        ))}
      </SimpleGrid>
    </Flex>
  );
};
